import { SidePanelDetailSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelDetailSectionTemplate";
import { useFreefieldState } from "shared/utils/freeFields/hooks";
import { GoogleMapLocation } from "components/GoogleMap/GoogleMapLocation";
import { ViewOnMap } from "components/GoogleMap/ViewOnMap";
import React, { useEffect, useState } from "react";
import { freeFieldEditableSections } from "show/components/freeFields/freeFieldEditableSections";
import { getFreefieldId, isFreefieldKey } from "shared/utils/freeFields/utils";
import { artistSection } from "show/detail/sections/artistSection";
import { hotelSection } from "show/detail/sections/hotelSection";
import { mainSection } from "show/detail/sections/mainSection";
import { promoterSection } from "show/detail/sections/promoterSection";
import { ticketsSection } from "show/detail/sections/ticketsSection";
import { venueSection } from "show/detail/sections/venueSection";
import { ShowFormState, getShowFieldMetaData, mapShowToShowFormState } from "shared/utils/fieldsHelper/showFields";
import { invalidateShowsCache } from "utilities/api/showQuery";
import { useQueryClient } from "@tanstack/react-query";
import { toCompanySmall } from "shared/utils/companyUtils";
import { useIsExternal } from "common/SessionContext";
import { useHasAnyTags } from "components/Tag/hooks";

interface ShowSidePanelDetailsProps {
  data: server.dto.ShowSidePanelData
  canEdit: boolean;
  refetch: () => Promise<void>
}

export const ShowSidePanelDetails = (props: ShowSidePanelDetailsProps) => {
  const isExternal = useIsExternal();
  const isInternal = !isExternal;

  const [show, setShow] = useState<server.dto.Show>(props.data.show);
  const [showInfo, setShowInfo] = useState<ShowFormState>(mapShowToShowFormState(props.data.show));
  const [tags, setTags] = useState<server.dto.TagSmall[]>(props.data.show.tags || []);
  const [products, setProducts] = useState<server.dto.ProductsByParent>(props.data.products);
  const [promoterCompany, setPromoterCompany] = useState<server.dto.Company>(props.data.promoterCompany);
  const [promoterContact, setPromoterContact] = useState<server.dto.Contact>(props.data.promoterContact);
  const [venueContact, setVenueContact] = useState<server.dto.Contact>(props.data.venueContact);
  const [hotelContact, setHotelContact] = useState<server.dto.Contact>(props.data.hotelContact);

  const ffState = useFreefieldState(props.data.show.freeFields, props.data.freeFieldCategories, props.data.freeFieldDefinitionPermissions);

  const hasTags = isInternal && useHasAnyTags("show");
  const fieldMetaData = getShowFieldMetaData(
    site.currentEnvironment,
    site.permissions,
    showInfo,
    show.permissions,
    show.validationStatus,
    hasTags
  );

  useEffect(() => {
    setShow(props.data.show);
    setShowInfo(mapShowToShowFormState(props.data.show));
    setTags(props.data.show.tags || []);
    setProducts(props.data.products);
    setPromoterCompany(props.data.promoterCompany);
    setPromoterContact(props.data.promoterContact);
    setVenueContact(props.data.venueContact);
    setHotelContact(props.data.hotelContact);
  }, [props.data]);

  const queryClient = useQueryClient();

  const onValueChanged = async (key: string, value: any) => {

    if (isFreefieldKey(key)) {
      ffState.setEditableFreefieldValue(getFreefieldId(key), value);
      return;
    } else if (key === "tags") {
      setTags(value);
    } else if (key === "artist") {
      setShowInfo(prev => ({ ...prev, artist: value, attendees: [] }));
    } else if (key === "venue") {
      setShowInfo(prev => ({ ...prev, venue: value, venueContact: null, capacity: null, doors: { open: null, close: null }, ageRestriction: null }));
      setVenueContact(null);
    } else if (key === "promoter") {
      setShowInfo(prev => ({ ...prev, promoter: toCompanySmall(value), promoterContact: null }));
      setPromoterContact(null);
      setPromoterCompany(value);
    } else if (key === "hotel") {
      setShowInfo(prev => ({ ...prev, hotel: value, hotelContact: null }));
      setHotelContact(null);
    } else if (key === "ageRestriction") {
      setShowInfo(prev => ({ ...prev, ageRestriction: value?.value ?? value }));
    } else if (key === "currencyAndExchangeRate") {
      setShowInfo(prev => ({ ...prev, currency: value.currency, exchangeRate: value.exchangeRate }));
    } else if (key === "promoterContact") {
      setPromoterContact(value);
      setShowInfo(prev => ({ ...prev, promoterContact: value }));
    } else if (key === "hotelContact") {
      setHotelContact(value);
      setShowInfo(prev => ({ ...prev, hotelContact: value }));
    } else if (key === "venueContact") {
      setVenueContact(value);
      setShowInfo(prev => ({ ...prev, venueContact: value }));
    } else {
      setShowInfo(prev => ({ ...prev, [key]: value }));
    }
  };

  const onSuccessfulUpdate = async (key: string, newValue: any) => {
    if (isFreefieldKey(key)) {
      return;
    }

    if (key === "promoterContact" && newValue != null) {
      window.CoreApi.CompanyToContact.SetLink({ companyId: showInfo.promoter.companyId, contactId: newValue.contactId, isActive: true });
    } else if (key === "hotelContact" && newValue != null) {
      window.CoreApi.CompanyToContact.SetLink({ companyId: showInfo.hotel.companyId, contactId: newValue.contactId, isActive: true });
    } else if (key === "venueContact" && newValue != null) {
      window.CoreApi.CompanyToContact.SetLink({ companyId: showInfo.venue.venueId, contactId: newValue.contactId, isActive: true });
    }

    await props.refetch();
    invalidateShowsCache(queryClient);
  };

  const canInPageEdit = props.canEdit && show.permissions.canModify;

  return <SidePanelDetailSectionTemplate
    isInPageEditable={canInPageEdit}
    onValueChanged={onValueChanged}
    onSuccessfulUpdate={onSuccessfulUpdate}
    sections={[
      {
        ...mainSection(show, showInfo, tags, isExternal, fieldMetaData, {
          exclusivityClause: props.data.exclusivityClause,
          products,
        }),
        hideTitle: true,
      },
      artistSection(show, showInfo, isExternal, fieldMetaData, true),
      venueSection(show, showInfo, venueContact, isExternal, fieldMetaData, true),
      promoterSection(show, showInfo, promoterCompany, promoterContact, isExternal, fieldMetaData, true),
      hotelSection(show, showInfo, hotelContact, isExternal, fieldMetaData, true),
      ticketsSection(show, showInfo, isExternal, fieldMetaData),
      ...freeFieldEditableSections(
        show.showId,
        ffState.freefieldCategoriesWithValuesAndPermissions,
        window.CoreApi.Show.UpdateFreeField,
      ),
    ]}
    additionalContent={<>
      {show.venue?.location && <>
        <ViewOnMap height={400}>
          <GoogleMapLocation
            pin={{
              type: "show",
              phase: "unknown"
            }}
            location={show.venue.location}
          />
        </ViewOnMap>
      </>}
    </>}
  />
}
